import axios from 'axios'
import type { AxiosRequestConfig } from 'axios'
import { toast } from 'sonner'

import {
  AUTH_STORAGE_KEY,
  IMPERSONATION_STORAGE_KEY,
  clearImpersonationToken,
  getAuthToken,
  setAuthToken,
  setImpersonationToken,
} from '@/shared/api/auth-storage'
import { PROJECT_NOT_READY_EVENT, projectIdFromSubresourceUrl } from '@/shared/api/errors'
import { getSocketId } from '@/shared/realtime/echo'
import type { ApiErrorPayload } from '@/shared/types/api'

export {
  AUTH_STORAGE_KEY,
  IMPERSONATION_STORAGE_KEY,
  clearImpersonationToken,
  getAuthToken,
  setAuthToken,
  setImpersonationToken,
}

// Даёт вызывающему коду подавить общий toast.error ниже для конкретного запроса —
// например массовая архивация сама показывает честный итог «X из Y» и не хочет
// вдобавок красного тоста на каждый упавший запрос в пачке.
declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressErrorToast?: boolean
  }
}

const baseURL = import.meta.env.VITE_API_BASE_URL?.trim() || 'http://localhost:8080/api/v1'

export const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken()

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  const socketId = getSocketId()
  if (socketId) {
    config.headers['X-Socket-Id'] = socketId
  }

  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status as number | undefined
    const payload = error?.response?.data as ApiErrorPayload | undefined
    const config = error?.config as AxiosRequestConfig | undefined

    if (status === 401) {
      window.dispatchEvent(new Event('ai-stone:unauthorized'))
    } else if (status === 404) {
      // Объекта уже нет (типовой случай — он удалён в этой же вкладке, а вдогонку
      // ушёл запрос подресурса). Показывать техтекст Laravel «No query results for
      // model [App\Models\Project] …» пользователю незачем.
    } else if (status === 423) {
      // Проект ещё собирается. Экран рисует по этому ответу заглушку с объяснением —
      // красный тост поверх неё только пугает. А когда 423 прилетел подресурсу уже
      // открытой страницы, сам проект лежит в кеше готовым: сообщаем вкладке, чтобы
      // она перепросила его и сменила экран на заглушку без перезагрузки.
      const projectId = projectIdFromSubresourceUrl(error?.config?.url)

      if (projectId) {
        window.dispatchEvent(new CustomEvent(PROJECT_NOT_READY_EVENT, { detail: { projectId } }))
      }
    } else if (status && status >= 500) {
      // Тело 5xx — это «Server Error» и подобное, не сообщение для пользователя.
      if (!config?.suppressErrorToast) {
        toast.error('Сервис временно недоступен. Попробуйте позже')
      }
    } else if (status && status >= 400) {
      if (!config?.suppressErrorToast) {
        toast.error(payload?.message || 'Ошибка запроса к API')
      }
    }

    return Promise.reject(error)
  },
)
